import React, { useEffect, useState } from 'react';
import { Grid, Paper, Typography, Box, useTheme, Skeleton } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StorageIcon from '@mui/icons-material/Storage';
import TimelineIcon from '@mui/icons-material/Timeline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningIcon from '@mui/icons-material/Warning';

import GraficoArmazenamento from './Armazenamento';
import UltimoArquivo from './UltimoArquivo';
import HistoricoRecente from './HistoricoRecente';
import { experienciaPremiumAtiva } from '../../App';
import PlanoClienteCard from '../PlanoCliente/PlanoClienteCard';
import CheckDBTimeline, { ICheckDBHistory } from './CheckDBTimeline';
import { useLogin } from '../../contexts/LoginContext';
import { apiServer } from '../../api';
import PremiumCard from '../ui/PremiumCard';
import StatusBadge from '../ui/StatusBadge';
import MetricCard from '../ui/MetricCard';
import moment from 'moment';

interface IUnidadeDisco {
  id: string;
  unidade: string;
  tamanhoTotal: string | number;
  tamanhoLivre: string | number;
  percentualUso: number;
  atualizadoEm: string;
}

interface IResumoDashboard {
  statusGeral: string;
  ultimoBackup: {
    arquivoID: string | number;
    nomeArquivo: string;
    dataHora: string;
    tamanhoGB: number;
    status: string;
    tarefa: string;
    statusAnomalia?: string;
  } | null;
  armazenamento: {
    usadoGB: number;
    totalGB: number;
    percentual: number;
    previsaoEsgotamentoDias: number;
  };
  execucoes: {
    sucessoUltimos7Dias: number;
    falhasUltimos7Dias: number;
    taxaSucesso: number;
  };
  proximaExecucao: {
    tarefaID: string | number;
    nome: string;
    dataHora: string;
  } | null;
  unidadesDisco?: IUnidadeDisco[];
  checkdbTimeline?: ICheckDBHistory[];
}

function DashboardPremium() {
  const [login] = useLogin();
  const theme = useTheme();
  const [resumo, setResumo] = useState<IResumoDashboard | null>(null);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<boolean>(false);

  const carregarResumo = React.useCallback(() => {
    const clienteID = login.usuario?.usuarioCliente?.clienteID;
    if (!clienteID || !login.accessToken) return;

    setCarregando(true);
    setErro(false);
    apiServer<IResumoDashboard>()
      .get(`/v2/dashboard/resumo-operacional/${clienteID}`, login.accessToken)
      .then((response) => {
        setResumo(response.data);
        setCarregando(false);
      })
      .catch((err) => {
        console.error('Erro ao buscar resumo v2', err);
        setErro(true);
        setCarregando(false);
      });
  }, [login.accessToken, login.usuario?.usuarioCliente?.clienteID]);

  useEffect(() => {
    carregarResumo();
  }, [carregarResumo]);

  if (carregando) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
        </Grid>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3 }} />
          </Grid>
        ))}
        <Grid item xs={12} md={8} lg={9}>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
        </Grid>
      </Grid>
    );
  }

  // Fallback seguro caso o endpoint v2 retorne erro
  if (erro || !resumo) {
    return <DashboardLegacy />;
  }

  const tempoAtras = resumo.ultimoBackup 
    ? moment(resumo.ultimoBackup.dataHora).fromNow()
    : 'Sem backups';

  return (
    <Grid container spacing={3}>
      {/* Top Banner de Saúde */}
      <Grid item xs={12}>
        <PremiumCard sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          borderLeft: `6px solid ${
            resumo.statusGeral === 'saudavel' ? theme.palette.success.main :
            resumo.statusGeral === 'atencao' ? theme.palette.warning.main :
            theme.palette.error.main
          }`
        }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {resumo.statusGeral === 'saudavel' ? 'Seu ambiente está protegido' : 
               resumo.statusGeral === 'atencao' ? 'Atenção operacional recomendada' : 
               'Ações corretivas críticas necessárias'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {resumo.ultimoBackup 
                ? `Último backup realizado com sucesso ${tempoAtras}${resumo.ultimoBackup.tarefa ? ` (${resumo.ultimoBackup.tarefa})` : ''}`
                : 'Nenhum histórico de backup cadastrado.'}
            </Typography>
          </Box>
          <StatusBadge status={resumo.statusGeral} />
        </PremiumCard>
      </Grid>

      {/* Alerta de Backup Anômalo/Suspeito */}
      {resumo.ultimoBackup && resumo.ultimoBackup.statusAnomalia && (
        <Grid item xs={12}>
          <Paper sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            borderLeft: `6px solid ${theme.palette.warning.main}`,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.15)' : '#fff8e1'
          }}>
            <WarningIcon color="warning" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                Aviso: Backup Recente com Tamanho Suspeito
              </Typography>
              <Typography variant="body2" color="text.secondary">
                O arquivo de backup <strong>{resumo.ultimoBackup.nomeArquivo}</strong> ({resumo.ultimoBackup.tamanhoGB} GB) da tarefa <strong>{resumo.ultimoBackup.tarefa}</strong> apresentou uma variação de tamanho incomum (
                {resumo.ultimoBackup.statusAnomalia === 'anomalo_queda' ? 'queda abrupta de tamanho' : 'aumento abrupto de tamanho'}
                ) em relação ao histórico recente de execuções. Verifique a integridade dos dados no servidor do cliente.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      )}

      {/* Alerta de Espaço Crítico em Disco das Unidades do Cliente */}
      {resumo.unidadesDisco && resumo.unidadesDisco.some(u => Number(u.percentualUso) > 70) && (
        <Grid item xs={12}>
          <Paper sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            borderLeft: `6px solid ${theme.palette.error.main}`,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.15)' : '#fdecea'
          }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                Aviso de Espaço Crítico em Disco Local (Agente)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                As seguintes unidades de disco do cliente estão com mais de 70% de uso (menos de 30% livre): 
                <strong>
                  {resumo.unidadesDisco
                    .filter(u => Number(u.percentualUso) > 70)
                    .map(u => ` ${u.unidade} (${u.percentualUso}% usado)`)
                    .join(', ')}
                </strong>. 
                Libere espaço nestas unidades para que as tarefas de backup possam realizar a compactação e geração de temporários locais com segurança.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      )}

      {/* Cards de Métricas Principais */}
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Status de Proteção"
          value={resumo.statusGeral === 'saudavel' ? 'Saudável' : resumo.statusGeral === 'atencao' ? 'Atenção' : 'Crítico'}
          subtitle="Taxa geral de integridade"
          icon={resumo.statusGeral === 'saudavel' ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
          progressColor={resumo.statusGeral === 'saudavel' ? 'success' : resumo.statusGeral === 'atencao' ? 'warning' : 'error'}
          progressValue={100}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Uso de Armazenamento"
          value={`${resumo.armazenamento.usadoGB} GB`}
          subtitle={`De ${resumo.armazenamento.totalGB} GB contratados`}
          icon={<StorageIcon />}
          progressColor={resumo.armazenamento.percentual > 90 ? 'error' : resumo.armazenamento.percentual > 75 ? 'warning' : 'primary'}
          progressValue={resumo.armazenamento.percentual}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Taxa de Sucesso"
          value={`${resumo.execucoes.taxaSucesso}%`}
          subtitle={`${resumo.execucoes.sucessoUltimos7Dias} sucessos e ${resumo.execucoes.falhasUltimos7Dias} falhas`}
          icon={<TimelineIcon />}
          progressColor={resumo.execucoes.taxaSucesso > 95 ? 'success' : resumo.execucoes.taxaSucesso > 85 ? 'warning' : 'error'}
          progressValue={resumo.execucoes.taxaSucesso}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Próxima Execução"
          value={resumo.proximaExecucao ? moment(resumo.proximaExecucao.dataHora).format('HH:mm') : '--:--'}
          subtitle={resumo.proximaExecucao ? resumo.proximaExecucao.nome : 'Sem agendamentos ativos'}
          icon={<ScheduleIcon />}
          progressColor="secondary"
          progressValue={100}
        />
      </Grid>

      {/* Gráfico */}
      <Grid item xs={12} md={8} lg={9}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 320 }}>
          <GraficoArmazenamento />
        </Paper>
      </Grid>

      {/* Último Backup */}
      <Grid item xs={12} md={4} lg={3}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 320 }}>
          <UltimoArquivo />
        </Paper>
      </Grid>

      {/* Unidades de Disco Local (Agente) */}
      {resumo.unidadesDisco && resumo.unidadesDisco.length > 0 && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 320, overflow: 'auto' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorageIcon color="primary" /> Espaço em Disco Local (Agente)
            </Typography>
            <Grid container spacing={2}>
              {resumo.unidadesDisco.map((unidade) => {
                const totalGB = Math.round((Number(unidade.tamanhoTotal) / 1024 / 1024 / 1024) * 10) / 10;
                const livreGB = Math.round((Number(unidade.tamanhoLivre) / 1024 / 1024 / 1024) * 10) / 10;
                const usadoGB = Math.round((totalGB - livreGB) * 10) / 10;
                const percentual = Number(unidade.percentualUso);
                
                const corProgresso = percentual > 90 ? theme.palette.error.main : 
                                     percentual > 70 ? theme.palette.warning.main : 
                                     theme.palette.success.main;

                return (
                  <Grid item xs={12} sm={6} key={unidade.unidade}>
                    <Box sx={{ p: 1.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Unidade {unidade.unidade}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {usadoGB} GB / {totalGB} GB ({percentual}%)
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100%', height: 8, backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#e0e0e0', borderRadius: 4, overflow: 'hidden', mb: 1 }}>
                        <Box sx={{ width: `${percentual}%`, height: '100%', backgroundColor: corProgresso, borderRadius: 4 }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color={percentual > 70 ? 'error' : 'text.secondary'}>
                          Livre: {livreGB} GB ({Math.round((100 - percentual) * 10) / 10}%)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ref: {moment(unidade.atualizadoEm).fromNow()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
      )}

      {/* Histórico do CheckDB */}
      <Grid item xs={12} md={resumo.unidadesDisco && resumo.unidadesDisco.length > 0 ? 6 : 12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 320, overflow: 'auto' }}>
          <CheckDBTimeline timeline={resumo.checkdbTimeline || []} />
        </Paper>
      </Grid>

      {/* Meu Plano */}
      <Grid item xs={12}>
        <PlanoClienteCard variant="dashboard" />
      </Grid>

      {/* Histórico Recente */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <HistoricoRecente />
        </Paper>
      </Grid>
    </Grid>
  );
}

function DashboardLegacy() {
  return (
    <Grid container spacing={3}>
      {/* Chart */}
      <Grid item xs={12} md={8} lg={9}>
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: 300,
          }}
        >
          <GraficoArmazenamento />
        </Paper>
      </Grid>
      {/* Recent Deposits */}
      <Grid item xs={12} md={4} lg={3}>
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: 300,
          }}
        >
          <UltimoArquivo />
        </Paper>
      </Grid>
      {/* Recent Orders */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <HistoricoRecente />
        </Paper>
      </Grid>
    </Grid>
  );
}

export default function Dashboard() {
  return experienciaPremiumAtiva ? <DashboardPremium /> : <DashboardLegacy />;
}